import avatarNoImg from '@/../public/img/avatarNoImg.jpg';
import { originalProviderIcons, svgIconsTyped, timezones } from '@/app/constants';
import { Aggregator } from '@alien-platform/bo-sdk';
import { AdminRoles, Permission } from '@alien-platform/bo-sdk-v2';
import { unparse } from 'papaparse';
import { z, ZodTypeAny } from 'zod';

import { CustomConfigInputProps, PermissionsItem, UserSession } from '@/types/general';

import { getBoInstance } from './bo-instance';
import { getBoSdkInstance } from './bo-sdk-instance';
import { getBoSdkV2Instance } from './bo-sdk-v2-instance';

export const checkAdminRoleFromUser = (user: UserSession | null, adminRole: AdminRoles[]): boolean => {
  if (!user?.role) return false;

  const userRole = user.role;

  // If adminRole is an array, check if any role in the array exists in the user's roles
  if (Array.isArray(adminRole)) {
    return adminRole.includes(userRole);
  }

  // If adminRole is a single role, check if it exists in the user's roles
  return userRole === adminRole;
};

export const checkPermissionsClient = ({
  componentId,
  type,
}: {
  componentId: string;
  type?: PermissionsItem;
}): boolean => {
  let permissions: Permission[] | null = null;

  if (typeof document !== 'undefined') {
    if (window.userPermissions) {
      permissions = window?.userPermissions;
    }
  }

  if (!permissions || !Array.isArray(permissions)) {
    return false;
  }

  // Recursive search function
  const hasPermission = (permissionList: Permission[]): boolean => {
    for (const perm of permissionList) {
      // Type: MENU  check requiredMenuItems

      if (type === PermissionsItem.MENU && perm.requiredMenuItems?.includes(componentId?.toLowerCase())) {
        return true;
      }

      // // Type: default or specific check name match and checked status
      // if (perm.name === componentId && perm.) {
      //   return true;
      // }

      // // Check subPermissions recursively
      // if (perm.subPermissions && perm.subPermissions.length > 0) {
      //   if (hasPermission(perm.subPermissions)) {
      //     return true;
      //   }
      // }
    }
    return false;
  };

  return hasPermission(permissions);
};

export const checkBelongingBrand = async (opId: string, idToken: string, selectedOpId: string): Promise<boolean> => {
  try {
    const boInstance = getBoInstance();
    const data = await boInstance.getAllBrands({ parentOpId: opId, page: 1, limit: 1000 }, idToken);
    if (data.success && data.data) {
      const belongingBrand = data.data.operators.find((item) => item.opId === selectedOpId);
      if (!belongingBrand) return false;

      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
};

export const checkAdminRoleFromRoles = (roles: AdminRoles, adminRole: AdminRoles[]): boolean => {
  // If adminRole is an array, check if any role in the array exists in the user's roles
  if (Array.isArray(adminRole)) {
    return adminRole.some((role) => roles?.includes(role));
  }

  // If adminRole is a single role, check if it exists in the user's roles
  return roles.includes(adminRole);
};

// Renders a user friendly player code if admin is not super admin
export const fullPlayerCode = (playerCode: string, opId: string): string => {
  const includesOpId = playerCode.includes(`B2C`) || playerCode.includes('BR');

  if (includesOpId) return playerCode;

  return `${opId}-${playerCode}`;
};

export async function getProtectedOpId(
  userOpId: string,
  bodyOpId: string,
  isSuperAdmin: boolean,
  idToken: string
): Promise<string> {
  let opId = userOpId;

  if (isSuperAdmin) {
    // Allow any opId if the user is a super admin
    opId = bodyOpId || '*';
  } else if ((await checkBelongingBrand(userOpId, idToken, bodyOpId)) || bodyOpId === userOpId) {
    // Non-super admin users must pass the belonging check or use their own opId
    opId = bodyOpId;
  }

  return opId;
}

export const exportCSVWithPapaParse = (title: string, data: unknown[]): void => {
  try {
    const csv = unparse(data, { header: true });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${title}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (e) {
    // console.log(e);
  }
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      resolve(reader.result as string);
    };
  });
};

export function capitalizeString(input: string): string {
  if (!input) return '';
  const updatedInput = input.replace(/_/g, ' ');

  return updatedInput
    .split(' ') // Split the string into words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
    .join(' '); // Join the words back into a single string
}

export const formatCurrencyCode = (code: string): string => {
  switch (code) {
    case 'CUSD':
      return 'cUSD';
    case 'IRR':
      return 'IRT';

    default:
      return code;
  }
};

const adjustDateWithOffset = (date: string, offsetMinutes: number): string => {
  const adjustedDate = new Date(date);
  adjustedDate.setMinutes(adjustedDate.getMinutes() + offsetMinutes);
  return adjustedDate.toISOString();
};

export const adjustDateWithTimeZone = (
  fromDate: string,
  toDate: string,
  timezone: string
): { adjustedFromDate: string; adjustedToDate: string } | null => {
  const offset = Object.values(timezones).find((item) => item.value === timezone)?.offset;
  if (offset) {
    const [hours, minutes] = offset.split(':').map(Number);
    const totalOffsetMinutes = hours * 60 + minutes;
    const adjustedFromDate = adjustDateWithOffset(fromDate, totalOffsetMinutes);
    const adjustedToDate = adjustDateWithOffset(toDate, totalOffsetMinutes);
    return { adjustedFromDate, adjustedToDate };
  } else if (timezone === 'UTC') {
    const adjustedFromDate = adjustDateWithOffset(fromDate, 0);
    const adjustedToDate = adjustDateWithOffset(toDate, 0);
    return { adjustedFromDate, adjustedToDate };
  }
  return null;
};

export const checkPermissionsApi = async ({ componentId, token }: { componentId: string; token: string }) => {
  // if (userId.includes('az-connection')) {
  //   userId = `${userId.split('|').pop()}`
  // } else {
  //   userId = userId.split('|')[1]
  // }

  const sdk = await getBoSdkV2Instance();

  let userPermissions = await sdk.auth.validateRequestByToken(token, componentId, [
    AdminRoles.SUPER_ADMIN,
    AdminRoles.ADMIN,
    AdminRoles.OPERATOR_ADMIN,
    AdminRoles.OPERATOR_MANAGER,
  ]);

  if (userPermissions.success && userPermissions.data) {
    return true;
  } else {
    return false;
  }

  // let updatedPermissions = await getPermissions(opId, userId)
  // if (updatedPermissions) {
  //   addToLocalPermissions(userId, updatedPermissions)
  //   if (updatedPermissions?.[componentId]) {
  //     return true
  //   } else {
  //     return false
  //   }
  // } else {
  //   return false
  // }
};

export function formatCamelCase(str: string): string {
  if (!str || str === '') return '';
  // eslint-disable-next-line prefer-named-capture-group
  const spacedString = str.replace(/([a-z])([A-Z])/g, '$1 $2');
  return spacedString.charAt(0).toUpperCase() + spacedString.slice(1);
}

export const mapCurrencyIcon = (currency: string): string | undefined => {
  const key = `svg${currency}`;
  let svgIcon: string | undefined = (svgIconsTyped as Record<string, { src: string }>)[key]?.src;

  switch (currency) {
    case 'IRR':
      svgIcon = svgIconsTyped['svgIRT']?.src;
      break;

    case 'LIF3':
    case 'L3USD':
    case 'CUSD':
    case 'MATIC':
    case 'BTC':
      svgIcon = `https://alienrates.imoon.com/icons/${currency}.png`;
      break;

    default:
      svgIcon = (svgIconsTyped as Record<string, { src: string }>)[key]?.src;
      break;
  }
  return svgIcon;
};

export const mapOriginalProviderIcon = (originalProvider: string): string => {
  const providerIconsMap: Record<string, { src: string }> = originalProviderIcons;

  // Normalize the provider name by removing spaces, apostrophes, and converting to lowercase
  const normalizedProvider = originalProvider.replace(/[\s']/g, '').toLowerCase();

  // Map to the corresponding icon
  const icon: string = providerIconsMap[normalizedProvider]?.src || avatarNoImg.src;

  return icon;
};

export function bytesToSize(bytes: number, decimals = 2): string {
  if (bytes === 0) {
    return '0 Bytes';
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i] || sizes[1]}`;
}

export function camelCaseToWords(str: string): string {
  if (!str) return '';
  // eslint-disable-next-line prefer-named-capture-group
  return str.replace(/([a-z])([A-Z])/g, '$1 $2');
}

export const getDomainServer = async (headersList: Headers, isOrigin?: string): Promise<string> => {
  const domain = headersList.get('x-clf-host') || headersList.get('x-forwarded-host');

  if (isOrigin === 'origin') {
    const originURL = domain?.includes('localhost') ? `http://${domain}` : `https://${domain}`;

    return originURL?.replace('www.', '');
  }

  const isDevDomain = domain?.includes('localhost') ? `${process.env.NEXT_PUBLIC_CUSTOM_DOMAIN}` : domain;

  return isDevDomain?.replace('www.', '') || '';
};

export const getSafeString = (value: unknown): string => (typeof value === 'string' ? value : '');
export const getEncodedString = (value: unknown): string =>
  typeof value === 'string' ? encodeURIComponent(value) : '';

export const buildZodSchemaFromFields = (fields: CustomConfigInputProps[]): z.ZodObject<Record<string, ZodTypeAny>> => {
  const schemaShape: Record<string, ZodTypeAny> = {};

  fields.forEach((field) => {
    schemaShape[field.name] = field.zod ?? z.any().optional();
  });

  return z.object(schemaShape);
};

export const containsFarsiCharacters = (s: string): boolean => {
  return /[\u0600-\u06FF]/.test(s);
};

export const aggregatorMap = Object.fromEntries(Object.entries(Aggregator).map(([key, value]) => [value, key]));

export function isStrictValidUrl(string: string) {
  try {
    const url = new URL(string);
    return url.protocol === 'https:'; // Only allow HTTPS URLs
  } catch (_) {
    return false;
  }
}
